import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaUser, FaArrowLeft } from "react-icons/fa";

import adminService from "../../../api/services/admin";
import {
  SectionHeader,
  SectionTitle,
  BackButton,
  PageCard,
  PageCardBody,
  LoadingContainer,
  ErrorState,
} from "../shared/DetailPage.styles";
import {
  ModalGrid,
  UserDetailsHeader,
  UserDetailsAvatar,
  UserDetailsInfo,
  UserDetailsName,
  UserDetailsId,
  ContactInfoGrid,
  InfoField,
  InfoLabel,
  InfoValue,
  StatsGrid,
  StatCard,
  StatValue,
  StatLabel,
  RegistrationSection,
} from "../../../components/UserDetailsModal/UserDetailsModal.styles";

const AdminUserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);

    adminService
      .getUserById(userId)
      .then((response) => {
        if (mounted) setUser(response.user);
      })
      .catch(() => {
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  const goBack = () => navigate("/admin-panel/korisnici");

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return "0";
    return new Intl.NumberFormat("sr-latn-RS").format(amount);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <l-line-spinner size="40" stroke="3" speed="1" color="var(--accent)" />
      </LoadingContainer>
    );
  }

  if (error || !user) {
    return (
      <ErrorState>
        <h3>Korisnik nije pronađen</h3>
        <p>Korisnik ne postoji ili je u međuvremenu obrisan.</p>
        <BackButton onClick={goBack}>
          <FaArrowLeft />
          Nazad na korisnike
        </BackButton>
      </ErrorState>
    );
  }

  const completedCount = user.completedCourses || 0;
  const hasName = user?.firstName?.trim() && user?.lastName?.trim();

  return (
    <>
      <SectionHeader>
        <BackButton onClick={goBack}>
          <FaArrowLeft />
          Nazad
        </BackButton>
        <SectionTitle>
          <FaUser />
          Detalji korisnika
        </SectionTitle>
      </SectionHeader>

      <PageCard>
        <PageCardBody>
          <ModalGrid>
            <UserDetailsHeader>
              <UserDetailsAvatar>
                {hasName
                  ? `${user.firstName?.[0]}${user.lastName?.[0]}`
                  : user?.email?.slice(0, 2)}
              </UserDetailsAvatar>
              <UserDetailsInfo>
                <UserDetailsName>
                  {hasName
                    ? `${user.firstName} ${user.lastName}`
                    : "Nepoznat Korisnik"}
                </UserDetailsName>
                <UserDetailsId>ID: #{user.id.slice(0, 8)}</UserDetailsId>
              </UserDetailsInfo>
            </UserDetailsHeader>

            <ContactInfoGrid>
              <InfoField>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{user.email}</InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Telefon</InfoLabel>
                <InfoValue>{user.phone || "Nije uneto"}</InfoValue>
              </InfoField>
            </ContactInfoGrid>

            <StatsGrid>
              <StatCard>
                <StatValue>{user.totalCourses || 0}</StatValue>
                <StatLabel>Ukupno kurseva</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue
                  style={{
                    color:
                      completedCount > 0
                        ? "var(--success)"
                        : "var(--text-secondary)",
                  }}
                >
                  {completedCount}
                </StatValue>
                <StatLabel>Završeno kurseva</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>
                  {formatCurrency(user.totalSpent)}{" "}
                  <StatValue $small>RSD</StatValue>
                </StatValue>
                <StatLabel>Potrošeno</StatLabel>
              </StatCard>
            </StatsGrid>

            <RegistrationSection>
              <InfoField>
                <InfoLabel>Datum registracije</InfoLabel>
                <InfoValue>
                  {new Date(user.createdAt).toLocaleDateString("sr-Latn-RS", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Poslednja aktivnost</InfoLabel>
                <InfoValue>
                  {user.lastLoginAt
                    ? new Date(user.lastLoginAt).toLocaleDateString(
                        "sr-Latn-RS",
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        },
                      )
                    : "Nikad se nije prijavila/o"}
                </InfoValue>
              </InfoField>
              <InfoField>
                <InfoLabel>Status</InfoLabel>
                <InfoValue
                  style={{
                    color: user.isActive ? "var(--success)" : "var(--error)",
                    fontWeight: "600",
                  }}
                >
                  {user.isActive ? "Aktivan" : "Neaktivan"}
                </InfoValue>
              </InfoField>
            </RegistrationSection>

            {user.courseEnrollments?.length > 0 && (
              <div>
                <InfoLabel style={{ marginBottom: "12px", display: "block" }}>
                  Kupljeni kursevi
                </InfoLabel>
                <ContactInfoGrid>
                  {user.courseEnrollments.map((enrollment) => (
                    <InfoField key={enrollment.id}>
                      <InfoValue>{enrollment.course?.title}</InfoValue>
                      <InfoLabel>
                        {enrollment.purchaseDate
                          ? new Date(
                              enrollment.purchaseDate,
                            ).toLocaleDateString("sr-Latn-RS")
                          : ""}
                      </InfoLabel>
                    </InfoField>
                  ))}
                </ContactInfoGrid>
              </div>
            )}
          </ModalGrid>
        </PageCardBody>
      </PageCard>
    </>
  );
};

export default AdminUserDetails;
